import React, { useState, useMemo, useEffect } from 'react';
import {
  XMarkIcon,
  BookOpenIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  ScaleIcon,
  ClipboardDocumentCheckIcon,
  CheckIcon,
  InformationCircleIcon,
  SparklesIcon,
  TagIcon,
  ExclamationCircleIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import {
  NOTAS_CAPITULOS_LIGIE,
  type NotaLegalCapitulo,
  obtenerNotasCapitulo,
} from '../../paginas/panel-usuario/tarifas/notasCapitulosLigie';

export interface ModalNotasCapituloProps {
  abierto: boolean;
  onCerrar: () => void;
  capituloInicial?: string;
}

// Esquemas de colores dinámicos por grupo de Secciones para los iconos iOS
const OBTENER_ESTILO_SECCION = (seccion: string): { gradiente: string; shadow: string; tagColor: string } => {
  switch (seccion) {
    case 'I':
    case 'II':
    case 'III':
    case 'IV':
      return {
        gradiente: 'bg-gradient-to-br from-emerald-400 via-teal-500 to-emerald-700',
        shadow: 'shadow-emerald-500/25',
        tagColor: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
      };
    case 'V':
      return {
        gradiente: 'bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-600',
        shadow: 'shadow-amber-500/25',
        tagColor: 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300',
      };
    case 'VI':
    case 'VII':
      return {
        gradiente: 'bg-gradient-to-br from-violet-500 via-purple-500 to-indigo-600',
        shadow: 'shadow-purple-500/25',
        tagColor: 'border-purple-500/30 bg-purple-500/10 text-purple-700 dark:text-purple-300',
      };
    case 'VIII':
    case 'IX':
    case 'X':
    case 'XI':
    case 'XII':
      return {
        gradiente: 'bg-gradient-to-br from-orange-400 via-amber-500 to-orange-600',
        shadow: 'shadow-orange-500/25',
        tagColor: 'border-orange-500/30 bg-orange-500/10 text-orange-700 dark:text-orange-300',
      };
    case 'XIII':
    case 'XIV':
    case 'XV':
      return {
        gradiente: 'bg-gradient-to-br from-slate-500 via-zinc-600 to-slate-800',
        shadow: 'shadow-slate-500/25',
        tagColor: 'border-slate-500/30 bg-slate-500/10 text-slate-700 dark:text-slate-300',
      };
    case 'XVI':
      return {
        gradiente: 'bg-gradient-to-br from-blue-500 via-sky-500 to-indigo-600',
        shadow: 'shadow-sky-500/25',
        tagColor: 'border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300',
      };
    case 'XVII':
    case 'XVIII':
      return {
        gradiente: 'bg-gradient-to-br from-cyan-400 via-teal-500 to-blue-600',
        shadow: 'shadow-cyan-500/25',
        tagColor: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-700 dark:text-cyan-300',
      };
    case 'XIX':
    case 'XX':
    case 'XXI':
    case 'XXII':
    default:
      return {
        gradiente: 'bg-gradient-to-br from-rose-500 via-pink-600 to-purple-600',
        shadow: 'shadow-rose-500/25',
        tagColor: 'border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300',
      };
  }
};

export const ModalNotasCapitulo: React.FC<ModalNotasCapituloProps> = ({
  abierto,
  onCerrar,
  capituloInicial = '01',
}) => {
  const [capituloSeleccionado, setCapituloSeleccionado] = useState<string>(capituloInicial);
  const [pestanaActiva, setPestanaActiva] = useState<'capitulo' | 'seccion' | 'subpartida' | 'resumen'>('capitulo');
  const [busqueda, setBusqueda] = useState<string>('');
  const [seccionFiltro, setSeccionFiltro] = useState<string>('TODAS');
  const [copiado, setCopiado] = useState(false);

  // Sincronizar capítulo cuando cambia la prop capituloInicial
  useEffect(() => {
    if (capituloInicial) {
      const limpio = capituloInicial.replace(/\D/g, '').slice(0, 2).padStart(2, '0');
      if (NOTAS_CAPITULOS_LIGIE[limpio]) {
        setCapituloSeleccionado(limpio);
      }
    }
  }, [capituloInicial, abierto]);

  // Manejo de accesibilidad: Escape para cerrar y scroll-lock
  useEffect(() => {
    if (!abierto) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCerrar();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (scrollBarWidth > 0) {
      document.body.style.paddingRight = `${scrollBarWidth}px`;
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [abierto, onCerrar]);

  // Lista de todos los capítulos
  const todosCapitulos = useMemo(() => {
    return Object.values(NOTAS_CAPITULOS_LIGIE).sort(
      (a, b) => parseInt(a.capitulo, 10) - parseInt(b.capitulo, 10)
    );
  }, []);

  // Capítulos filtrados por texto y sección
  const capitulosFiltrados = useMemo(() => {
    return todosCapitulos.filter((c) => {
      const coincideSeccion = seccionFiltro === 'TODAS' || c.seccion === seccionFiltro;
      if (!coincideSeccion) return false;

      if (!busqueda.trim()) return true;
      const q = busqueda.toLowerCase().trim();
      return (
        c.capitulo.includes(q) ||
        c.titulo.toLowerCase().includes(q) ||
        c.seccion.toLowerCase() === q ||
        c.notasCapitulo.toLowerCase().includes(q) ||
        (c.notasSubpartida && c.notasSubpartida.toLowerCase().includes(q))
      );
    });
  }, [todosCapitulos, busqueda, seccionFiltro]);

  // Capítulo activo
  const capituloActivo: NotaLegalCapitulo = useMemo(() => {
    return obtenerNotasCapitulo(capituloSeleccionado) || todosCapitulos[0];
  }, [capituloSeleccionado, todosCapitulos]);

  const estiloActivo = useMemo(
    () => OBTENER_ESTILO_SECCION(capituloActivo.seccion),
    [capituloActivo.seccion]
  );

  const copiarTextoNotas = () => {
    let texto = `=== NOTAS LEGALES - CAPÍTULO ${capituloActivo.capitulo}: ${capituloActivo.titulo} ===\n`;
    texto += `Sección ${capituloActivo.seccion}: ${capituloActivo.seccionTitulo}\n\n`;
    texto += `--- NOTAS DEL CAPÍTULO ---\n${capituloActivo.notasCapitulo}\n\n`;
    if (capituloActivo.notasSubpartida) {
      texto += `--- NOTAS DE SUBPARTIDA ---\n${capituloActivo.notasSubpartida}\n\n`;
    }
    if (capituloActivo.notasSeccion) {
      texto += `--- NOTAS DE LA SECCIÓN ${capituloActivo.seccion} ---\n${capituloActivo.notasSeccion}\n\n`;
    }
    texto += `Fuente: Ley de los Impuestos Generales de Importación y de Exportación (LIGIE) - OMA`;

    navigator.clipboard.writeText(texto);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  };

  // Resaltador de términos clave en el texto legal oficial
  const renderTextoLegal = (texto: string) => {
    if (!texto) {
      return (
        <p className="text-xs italic text-slate-400 dark:text-zinc-500">
          No hay notas legales adicionales registradas para este apartado.
        </p>
      );
    }

    const parrafos = texto.split('\n\n');

    return (
      <div className="space-y-3.5 font-mono text-xs leading-relaxed text-slate-700 dark:text-zinc-300">
        {parrafos.map((parrafo, idx) => {
          // Detectar si es un encabezado o número de nota
          const esNumeroNota = /^(?:[0-9]{1,2}\.|\b[a-z]\)|\([a-z0-9]+\)|[A-Z]\.)/.test(parrafo);
          const tieneExclusion = /no comprende|excepto|se excluyen/i.test(parrafo);
          const tieneInclusion = /comprende|se consideran|se entiende por|incluyen/i.test(parrafo);

          return (
            <div
              key={idx}
              className={`p-3.5 rounded-xl border transition-colors ${
                tieneExclusion
                  ? 'border-rose-500/20 bg-rose-500/5 dark:bg-rose-950/10'
                  : tieneInclusion
                  ? 'border-emerald-500/20 bg-emerald-500/5 dark:bg-emerald-950/10'
                  : 'border-slate-200/60 bg-slate-50/50 dark:border-zinc-800/60 dark:bg-zinc-900/30'
              }`}
            >
              <div className="flex items-start gap-2">
                {esNumeroNota && (
                  <span className="shrink-0 font-bold text-sky-600 dark:text-sky-400">
                    §
                  </span>
                )}
                <div className="flex-1 whitespace-pre-wrap">{parrafo}</div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (!abierto) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-notas-titulo"
      className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 md:p-6 animate-fadeIn"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-slate-900/80 backdrop-blur-md transition-opacity"
        onClick={onCerrar}
      />

      {/* Dock Master-Detail Modal */}
      <div className="relative flex flex-col w-full max-w-6xl h-[92vh] max-h-[880px] overflow-hidden rounded-[26px] border border-slate-200/80 bg-white shadow-2xl dark:border-zinc-800 dark:bg-[#0c0d10] z-10">
        {/* Barra Superior Global */}
        <div className="flex items-center justify-between border-b border-slate-200/80 px-4 sm:px-6 py-3.5 dark:border-zinc-800 bg-slate-50/80 dark:bg-zinc-900/50 backdrop-blur shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-[12px] bg-gradient-to-br from-indigo-500 via-sky-600 to-blue-700 text-white shadow-md shadow-sky-500/20">
              <BookOpenIcon className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-black uppercase tracking-widest text-sky-600 dark:text-sky-400">
                  LIGIE DOF & OMA
                </span>
                <span className="rounded-full bg-slate-200/70 dark:bg-zinc-800 px-2 py-0.5 text-[9px] font-bold text-slate-700 dark:text-zinc-300">
                  98 Capítulos • 22 Secciones
                </span>
              </div>
              <h2
                id="modal-notas-titulo"
                className="text-sm sm:text-base font-bold text-slate-900 dark:text-zinc-100"
              >
                Compendio Oficial de Notas Legales de Sección y Capítulo
              </h2>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={copiarTextoNotas}
              className="hidden sm:inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 shadow-xs transition hover:bg-slate-50 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700 cursor-pointer"
              title="Copiar texto oficial del capítulo activo"
            >
              {copiado ? (
                <>
                  <CheckIcon className="h-4 w-4 text-emerald-500" />
                  <span className="text-emerald-600 dark:text-emerald-400">Copiado</span>
                </>
              ) : (
                <>
                  <ClipboardDocumentCheckIcon className="h-4 w-4 text-slate-500 dark:text-zinc-400" />
                  <span>Copiar Notas</span>
                </>
              )}
            </button>

            <button
              type="button"
              onClick={onCerrar}
              className="rounded-xl p-1.5 text-slate-400 hover:bg-slate-200/50 hover:text-slate-700 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-200 transition-colors cursor-pointer"
              aria-label="Cerrar modal"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Cuerpo Principal Dividido: Sidebar Dock + Inspector Dossier */}
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Panel Izquierdo: Rail de Selección de Capítulos */}
          <div className="w-80 sm:w-96 flex flex-col border-r border-slate-200/80 dark:border-zinc-800 bg-slate-50/40 dark:bg-zinc-950/40 shrink-0">
            {/* Buscador de Capítulos */}
            <div className="p-3.5 border-b border-slate-200/80 dark:border-zinc-800 space-y-2.5">
              <div className="relative flex items-center">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 h-4 w-4 text-slate-400 dark:text-zinc-500" />
                <input
                  type="text"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  placeholder="Buscar capítulo, término o exclusión..."
                  className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-xs font-medium text-slate-900 placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-100 dark:placeholder:text-zinc-500"
                />
                {busqueda && (
                  <button
                    onClick={() => setBusqueda('')}
                    className="absolute right-2.5 text-xs text-slate-400 hover:text-slate-600 dark:hover:text-zinc-300"
                  >
                    ×
                  </button>
                )}
              </div>

              {/* Selector Rápido de Sección */}
              <div className="flex items-center gap-1.5 overflow-x-auto custom-scrollbar pb-1 text-[10px] font-bold">
                <button
                  onClick={() => setSeccionFiltro('TODAS')}
                  className={`px-2.5 py-1 rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                    seccionFiltro === 'TODAS'
                      ? 'bg-sky-600 text-white shadow-xs'
                      : 'bg-white text-slate-600 border border-slate-200 dark:bg-zinc-900 dark:text-zinc-400 dark:border-zinc-800 hover:border-slate-300'
                  }`}
                >
                  TODAS
                </button>
                {['I', 'II', 'IV', 'VI', 'VII', 'XI', 'XV', 'XVI', 'XVII', 'XVIII', 'XXII'].map(
                  (sec) => (
                    <button
                      key={sec}
                      onClick={() => setSeccionFiltro(sec)}
                      className={`px-2 py-1 rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                        seccionFiltro === sec
                          ? 'bg-sky-600 text-white shadow-xs'
                          : 'bg-white text-slate-600 border border-slate-200 dark:bg-zinc-900 dark:text-zinc-400 dark:border-zinc-800 hover:border-slate-300'
                      }`}
                    >
                      Sec. {sec}
                    </button>
                  )
                )}
              </div>

              <div className="flex items-center justify-between text-[10px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-widest px-1">
                <span>Capítulos ({capitulosFiltrados.length})</span>
                <span>Regla General 1</span>
              </div>
            </div>

            {/* Lista Scrollable de Capítulos */}
            <div className="flex-1 overflow-y-auto custom-scrollbar p-2.5 space-y-1.5">
              {capitulosFiltrados.map((cap) => {
                const seleccionado = cap.capitulo === capituloActivo.capitulo;
                const estilo = OBTENER_ESTILO_SECCION(cap.seccion);

                return (
                  <button
                    key={cap.capitulo}
                    type="button"
                    onClick={() => {
                      setCapituloSeleccionado(cap.capitulo);
                    }}
                    className={`w-full text-left rounded-xl p-2.5 transition-all flex items-center gap-3 cursor-pointer group ${
                      seleccionado
                        ? 'border border-sky-400/80 bg-sky-50/70 shadow-sm dark:border-sky-500/40 dark:bg-sky-950/20'
                        : 'border border-transparent hover:border-slate-200 hover:bg-white dark:hover:border-zinc-800 dark:hover:bg-zinc-900/60'
                    }`}
                  >
                    {/* iOS Squircle Icon con número de Capítulo */}
                    <div
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] text-white font-mono font-black text-xs shadow-md transition-transform group-hover:scale-105 ${estilo.gradiente} ${estilo.shadow}`}
                    >
                      {cap.capitulo}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1 mb-0.5">
                        <span className="text-[10px] font-black uppercase tracking-wider text-slate-500 dark:text-zinc-400">
                          Sec. {cap.seccion}
                        </span>
                        {cap.notasSubpartida && (
                          <span className="text-[9px] font-bold px-1.5 py-0.2 rounded-sm bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-500/20">
                            Subpartida
                          </span>
                        )}
                      </div>
                      <h4
                        className={`text-xs font-bold truncate leading-tight ${
                          seleccionado
                            ? 'text-sky-950 dark:text-zinc-100'
                            : 'text-slate-800 dark:text-zinc-300'
                        }`}
                        title={cap.titulo}
                      >
                        {cap.titulo}
                      </h4>
                      <p className="text-[10px] text-slate-400 dark:text-zinc-500 truncate mt-0.5 font-sans">
                        {cap.criterioClave}
                      </p>
                    </div>

                    <ChevronRightIcon
                      className={`h-4 w-4 shrink-0 transition-transform ${
                        seleccionado
                          ? 'text-sky-600 dark:text-sky-400 translate-x-0.5'
                          : 'text-slate-300 dark:text-zinc-600 group-hover:translate-x-0.5'
                      }`}
                    />
                  </button>
                );
              })}

              {capitulosFiltrados.length === 0 && (
                <div className="py-12 text-center text-xs text-slate-400 dark:text-zinc-500 font-medium">
                  No se encontraron capítulos con "{busqueda}".
                </div>
              )}
            </div>
          </div>

          {/* Panel Derecho: Dossier Inspector */}
          <div className="flex-1 flex flex-col min-h-0 bg-white dark:bg-[#0e0f14] overflow-hidden">
            {/* Header del Capítulo Activo */}
            <div className="p-5 sm:p-6 border-b border-slate-200/80 dark:border-zinc-800 bg-slate-50/30 dark:bg-zinc-900/20 shrink-0">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span
                  className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-black tracking-wider uppercase border ${estiloActivo.tagColor}`}
                >
                  <TagIcon className="h-3 w-3" />
                  Capítulo {capituloActivo.capitulo}
                </span>

                <span className="rounded-full bg-slate-100 dark:bg-zinc-800 px-2.5 py-0.5 text-[10px] font-bold text-slate-600 dark:text-zinc-400 border border-slate-200/60 dark:border-zinc-700">
                  Sección {capituloActivo.seccion}: {capituloActivo.seccionTitulo}
                </span>
              </div>

              <h3 className="text-lg sm:text-xl font-black tracking-tight text-slate-900 dark:text-white leading-snug">
                {capituloActivo.titulo}
              </h3>

              {/* Selector de Pestañas del Inspector */}
              <div className="flex items-center gap-2 mt-4 border-b border-slate-200 dark:border-zinc-800 pb-0">
                <button
                  type="button"
                  onClick={() => setPestanaActiva('capitulo')}
                  className={`px-3.5 py-2 text-xs font-bold uppercase tracking-wide border-b-2 transition-all cursor-pointer ${
                    pestanaActiva === 'capitulo'
                      ? 'border-sky-500 text-sky-600 dark:border-sky-400 dark:text-sky-400'
                      : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-zinc-400 dark:hover:text-zinc-200'
                  }`}
                >
                  Notas de Capítulo
                </button>

                <button
                  type="button"
                  onClick={() => setPestanaActiva('seccion')}
                  className={`px-3.5 py-2 text-xs font-bold uppercase tracking-wide border-b-2 transition-all cursor-pointer ${
                    pestanaActiva === 'seccion'
                      ? 'border-sky-500 text-sky-600 dark:border-sky-400 dark:text-sky-400'
                      : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-zinc-400 dark:hover:text-zinc-200'
                  }`}
                >
                  Notas de Sección {capituloActivo.seccion}
                </button>

                {capituloActivo.notasSubpartida && (
                  <button
                    type="button"
                    onClick={() => setPestanaActiva('subpartida')}
                    className={`px-3.5 py-2 text-xs font-bold uppercase tracking-wide border-b-2 transition-all cursor-pointer ${
                      pestanaActiva === 'subpartida'
                        ? 'border-sky-500 text-sky-600 dark:border-sky-400 dark:text-sky-400'
                        : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-zinc-400 dark:hover:text-zinc-200'
                    }`}
                  >
                    Notas de Subpartida
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => setPestanaActiva('resumen')}
                  className={`px-3.5 py-2 text-xs font-bold uppercase tracking-wide border-b-2 transition-all cursor-pointer ${
                    pestanaActiva === 'resumen'
                      ? 'border-sky-500 text-sky-600 dark:border-sky-400 dark:text-sky-400'
                      : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-zinc-400 dark:hover:text-zinc-200'
                  }`}
                >
                  Criterio Didáctico
                </button>
              </div>
            </div>

            {/* Contenido Scrollable de la Pestaña Activa */}
            <div className="flex-1 overflow-y-auto custom-scrollbar p-5 sm:p-6 space-y-6">
              {pestanaActiva === 'capitulo' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ScaleIcon className="h-4 w-4 text-sky-600 dark:text-sky-400" />
                      <h4 className="text-xs font-bold uppercase tracking-widest text-slate-800 dark:text-zinc-200">
                        Texto Legal Oficial (DOF / OMA)
                      </h4>
                    </div>
                    <span className="text-[10px] font-bold text-slate-400 dark:text-zinc-500">
                      Vigencia Sistema Armonizado
                    </span>
                  </div>

                  {renderTextoLegal(capituloActivo.notasCapitulo)}
                </div>
              )}

              {pestanaActiva === 'seccion' && (
                <div className="space-y-4">
                  <div className="p-4 rounded-2xl border border-indigo-500/20 bg-indigo-500/5 dark:bg-indigo-950/20 text-xs text-indigo-950 dark:text-indigo-200 flex items-start gap-3">
                    <InformationCircleIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400 shrink-0 mt-0.5" />
                    <div>
                      <strong className="block font-bold mb-1">
                        Prevalencia de las Notas de Sección (Regla General 1):
                      </strong>
                      Las Notas de Sección abarcan todos los capítulos comprendidos en la Sección{' '}
                      {capituloActivo.seccion}. Ninguna clasificación dentro de este capítulo puede
                      contravenir los textos o exclusiones fijados en estas notas.
                    </div>
                  </div>

                  {renderTextoLegal(
                    capituloActivo.notasSeccion ||
                      'Esta Sección no contiene notas preliminares específicas; la clasificación se rige directamente por las notas de cada uno de sus capítulos y por los textos de partida.'
                  )}
                </div>
              )}

              {pestanaActiva === 'subpartida' && capituloActivo.notasSubpartida && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-800 dark:text-zinc-200">
                    <DocumentTextIcon className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                    <h4>Reglas Específicas de Subpartida (6 Dígitos)</h4>
                  </div>
                  {renderTextoLegal(capituloActivo.notasSubpartida)}
                </div>
              )}

              {pestanaActiva === 'resumen' && (
                <div className="space-y-5">
                  {/* Bento Grid de Inclusiones vs Exclusiones */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Exclusiones Clave */}
                    <div className="rounded-2xl border border-rose-500/30 bg-rose-500/5 p-4 dark:bg-rose-950/10">
                      <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-rose-700 dark:text-rose-400 mb-3">
                        <ExclamationCircleIcon className="h-4 w-4" />
                        Exclusiones Legales Críticas
                      </div>
                      <p className="text-xs text-slate-600 dark:text-zinc-400 mb-3 leading-relaxed">
                        Mercancías que a simple vista parecerían de este capítulo pero la Ley
                        ordena clasificar en otros códigos:
                      </p>
                      {capituloActivo.exclusionesClave.length > 0 ? (
                        <ul className="space-y-2 text-xs text-slate-700 dark:text-zinc-300 font-medium">
                          {capituloActivo.exclusionesClave.map((exc, i) => (
                            <li
                              key={i}
                              className="p-2 rounded-lg bg-white/70 dark:bg-black/30 border border-rose-500/10"
                            >
                              {exc}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-xs italic text-slate-500">
                          Consulte el texto completo en la pestaña "Notas de Capítulo".
                        </p>
                      )}
                    </div>

                    {/* Alcance e Inclusiones */}
                    <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4 dark:bg-emerald-950/10">
                      <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-emerald-700 dark:text-emerald-400 mb-3">
                        <SparklesIcon className="h-4 w-4" />
                        Alcance y Criterio de Inclusión
                      </div>
                      <p className="text-xs text-slate-600 dark:text-zinc-400 mb-3 leading-relaxed">
                        Definiciones y alcances reconocidos oficialmente por la OMA para este
                        rubro:
                      </p>
                      {capituloActivo.inclusionesClave.length > 0 ? (
                        <ul className="space-y-2 text-xs text-slate-700 dark:text-zinc-300 font-medium">
                          {capituloActivo.inclusionesClave.map((inc, i) => (
                            <li
                              key={i}
                              className="p-2 rounded-lg bg-white/70 dark:bg-black/30 border border-emerald-500/10"
                            >
                              {inc}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-xs italic text-slate-500">
                          Consulte el texto oficial en la pestaña "Notas de Capítulo".
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Impacto Operativo para Almacén WMS y Aduanas */}
                  <div className="rounded-2xl border border-slate-200/80 bg-slate-50/70 p-4 dark:border-zinc-800 dark:bg-zinc-900/40">
                    <h5 className="text-xs font-black uppercase tracking-wider text-slate-900 dark:text-zinc-100 mb-2">
                      💡 Regla de Oro en Clasificación (Art. 2 LIGIE - Regla 1)
                    </h5>
                    <p className="text-xs leading-relaxed text-slate-600 dark:text-zinc-400">
                      Antes de recurrir a analogías (Regla 4) o reglas de combinaciones (Regla 3),
                      verifique siempre las notas de este capítulo. Una mercancía descrita en el
                      nombre del capítulo puede estar estrictamente excluida en sus notas y
                      pertenecer a otra sección.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer del Modal */}
            <div className="border-t border-slate-200/80 px-6 py-3.5 dark:border-zinc-800 bg-slate-50/50 dark:bg-zinc-900/30 flex items-center justify-between shrink-0">
              <span className="text-[11px] text-slate-500 dark:text-zinc-400 font-medium">
                Fundamento: Art. 1 y Art. 2 LIGIE (Diario Oficial de la Federación).
              </span>
              <button
                type="button"
                onClick={onCerrar}
                className="px-4 py-2 rounded-xl bg-slate-900 text-white dark:bg-white dark:text-slate-900 text-xs font-bold uppercase tracking-wider hover:opacity-90 transition-opacity cursor-pointer shadow-sm"
              >
                Cerrar Visor
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

